import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import config from '../../config';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Area, AreaChart, Legend as ReLegend,
} from 'recharts';
import { toast } from 'react-toastify';

const STATUS_COLORS = {
  completed: '#10b981',
  connected: '#3b82f6',
  dialing: '#f59e0b',
  initiated: '#f59e0b',
  rejected: '#ef4444',
  no_answer: '#f97316',
  failed: '#ef4444',
  unknown: '#9ca3af',
};

const STATUS_LABELS = {
  completed: 'Accepted',
  connected: 'Connected',
  dialing: 'Dialing',
  initiated: 'Initiated',
  rejected: 'Rejected',
  no_answer: 'Not Received',
  failed: 'Failed',
  unknown: 'Unknown',
};

const CALLER_TYPE_COLORS = {
  customer: '#3b82f6',
  driver: '#10b981',
  lead: '#f59e0b',
  unknown: '#9ca3af',
};

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const formatDuration = (seconds) => {
  if (!seconds) return '0s';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

const formatHour = (hour) => {
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

const formatDate = (dateStr) => {
  if (!dateStr || dateStr === 'unknown') return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  } catch {
    return dateStr;
  }
};

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div style={{
      background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
      padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: 13,
    }}>
      <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: 4 }}>{label}</div>
      {payload.map((entry, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: entry.color }} />
          <span style={{ color: '#6b7280' }}>{entry.name}:</span>
          <span style={{ fontWeight: 600, color: '#1f2937' }}>
            {formatter ? formatter(entry.value, entry.name) : entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const StatCard = ({ icon, label, value, sub, color = '#3b82f6' }) => (
  <div style={{
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10,
    padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, minWidth: 160,
  }}>
    <div style={{
      width: 40, height: 40, borderRadius: 10, background: `${color}15`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color, fontSize: 16, flexShrink: 0,
    }}>
      <i className={`fa ${icon}`} />
    </div>
    <div>
      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: '#1f2937', lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>{sub}</div>}
    </div>
  </div>
);

const ChartCard = ({ title, children, height = 280 }) => (
  <div style={{
    background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
    padding: '16px 20px', flex: '1 1 340px', minWidth: 320,
  }}>
    <div style={{
      fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14,
      textTransform: 'uppercase', letterSpacing: '0.3px',
    }}>
      {title}
    </div>
    <div style={{ height }}>{children}</div>
  </div>
);

const CallLogsAnalytics = ({ callerType = 'all', fromDate = '', toDate = '', statusFilter = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${config.opsApiBase}/call-logs/analytics`, {
        params: {
          caller_type: callerType,
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
          status: statusFilter || undefined,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch call logs analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [callerType, fromDate, toDate, statusFilter]);

  useEffect(() => {
    if (!collapsed) fetchAnalytics();
  }, [fetchAnalytics, collapsed]);

  const summary = data?.summary;

  const hourlyData = useMemo(() =>
    (data?.calls_by_hour || []).map(r => ({ ...r, label: formatHour(r.hour) })),
  [data]);

  // Get all statuses that appear in hourly data for stacked bars
  const hourlyStatuses = useMemo(() => {
    const statuses = new Set();
    (data?.calls_by_hour || []).forEach(r => {
      Object.keys(r).forEach(k => {
        if (k !== 'hour' && k !== 'total' && k !== 'label') statuses.add(k);
      });
    });
    return Array.from(statuses);
  }, [data]);

  const dailyData = useMemo(() =>
    (data?.calls_by_day || []).map(r => ({ ...r, label: formatDate(r.date) })),
  [data]);

  const statusData = useMemo(() =>
    (data?.status_distribution || []).map(r => ({
      ...r, name: r.status.replace(/_/g, ' '),
      color: STATUS_COLORS[r.status] || '#9ca3af',
    })),
  [data]);

  const callerTypeData = useMemo(() =>
    (data?.caller_type_distribution || []).map(r => ({
      ...r, name: r.caller_type.charAt(0).toUpperCase() + r.caller_type.slice(1),
      color: CALLER_TYPE_COLORS[r.caller_type] || '#9ca3af',
    })),
  [data]);

  const durationData = useMemo(() =>
    (data?.avg_duration_by_type || []).map(r => ({
      ...r, name: r.caller_type.charAt(0).toUpperCase() + r.caller_type.slice(1),
      color: CALLER_TYPE_COLORS[r.caller_type] || '#9ca3af',
    })),
  [data]);

  return (
    <div style={{
      background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 12,
      marginBottom: 16, overflow: 'hidden',
    }}>
      <button
        onClick={() => setCollapsed(!collapsed)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          border: 'none', borderBottom: collapsed ? 'none' : '1px solid #e5e7eb',
          cursor: 'pointer', transition: 'all 0.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className="fa fa-bar-chart" style={{ color: '#6366f1', fontSize: 15 }} />
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1f2937' }}>Call Analytics</span>
          {loading && <i className="fa fa-spinner fa-spin" style={{ color: '#6b7280', fontSize: 13 }} />}
        </div>
        <i className={`fa fa-chevron-${collapsed ? 'down' : 'up'}`} style={{ color: '#9ca3af', fontSize: 12 }} />
      </button>

      {!collapsed && data && (
        <div style={{ padding: '16px 20px' }}>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
            <StatCard icon="fa-phone" label="Total Calls" value={summary?.total_calls || 0} color="#3b82f6" />
            <StatCard icon="fa-clock-o" label="Total Duration" value={formatDuration(summary?.total_duration_seconds)} color="#10b981" />
            <StatCard icon="fa-hourglass-half" label="Avg Duration" value={formatDuration(summary?.avg_duration_seconds)} color="#f59e0b" />
            <StatCard icon="fa-microphone" label="With Recording" value={summary?.with_recording || 0} sub={`${summary?.total_calls ? Math.round((summary.with_recording / summary.total_calls) * 100) : 0}%`} color="#8b5cf6" />
            <StatCard icon="fa-file-text-o" label="With Summary" value={summary?.with_summary || 0} sub={`${summary?.total_calls ? Math.round((summary.with_summary / summary.total_calls) * 100) : 0}%`} color="#ec4899" />
          </div>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <ChartCard title="Calls by Hour (by Status)">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hourlyData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} interval={2} />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} />
                  <ReTooltip content={<CustomTooltip />} />
                  <ReLegend wrapperStyle={{ fontSize: 11 }} />
                  {hourlyStatuses.map((status) => (
                    <Bar
                      key={status}
                      dataKey={status}
                      name={STATUS_LABELS[status] || status}
                      stackId="a"
                      fill={STATUS_COLORS[status] || '#9ca3af'}
                      radius={status === hourlyStatuses[hourlyStatuses.length - 1] ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Calls by Day">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={dailyData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} />
                  <ReTooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="count" name="Calls" stroke="#3b82f6" fill="#3b82f620" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Status Distribution">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%" innerRadius={50} outerRadius={90}
                    paddingAngle={2} dataKey="count" nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: '#9ca3af', strokeWidth: 1 }}>
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.color || PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <ReTooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            {callerType === 'all' && (
              <ChartCard title="Caller Type Breakdown">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={callerTypeData} margin={{ top: 5, right: 10, left: -15, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} allowDecimals={false} />
                    <ReTooltip content={<CustomTooltip />} />
                    <Bar dataKey="count" name="Calls" radius={[6, 6, 0, 0]}>
                      {callerTypeData.map((entry, i) => (
                        <Cell key={i} fill={entry.color || PIE_COLORS[i]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {callerType === 'all' && (
              <ChartCard title="Avg Duration by Caller Type" height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={durationData} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 10, fill: '#9ca3af' }} tickFormatter={(v) => formatDuration(v)} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#6b7280' }} width={80} />
                    <ReTooltip content={<CustomTooltip formatter={(v) => formatDuration(v)} />} />
                    <Bar dataKey="avg_duration" name="Avg Duration" radius={[0, 6, 6, 0]}>
                      {durationData.map((entry, i) => (
                        <Cell key={i} fill={entry.color || PIE_COLORS[i]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        </div>
      )}

      {!collapsed && loading && !data && (
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <i className="fa fa-spinner fa-spin" style={{ color: '#6b7280', fontSize: 20 }} />
          <p style={{ color: '#6b7280', fontSize: 13, margin: '8px 0 0' }}>Loading analytics...</p>
        </div>
      )}
    </div>
  );
};

export default CallLogsAnalytics;
